import { FormControlLabel, Switch, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useMutation, useQueryClient } from "react-query";
import { useState } from "react";

import { getBlogStatus, refetchQueries } from "@/utils";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { publishBlog } from "@/services/blog";
import { configs } from "@/configs";
import { formatBlogStatus } from "@/formatter";
import { BlogStatus } from "@/models";

import type { Blog } from "@/models";
import type { AxiosErrorWithData } from "@/client/api";

type BlogStatusSwitchProps = {
  data: Blog;
  fetchKeys?: string[];
};

export function BlogStatusSwitch({
  data,
  fetchKeys = [],
}: BlogStatusSwitchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const onChangeStatus = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const { mutate: publish, isLoading } = useMutation(publishBlog, {
    onSuccess: async () => {
      enqueueSnackbar("อัพเดทสถานะสำเร็จ", { variant: "success" });
      await refetchQueries({ queryClient, fetchKeys });
      onClose();
    },
    onError: (error: AxiosErrorWithData) => {
      console.error(error);
      enqueueSnackbar(
        error.response?.data.message ?? configs.unknownErrorMessage,
        { variant: "error" }
      );
    },
  });

  const { id, publishedAt } = data;
  const isPublished = !!publishedAt;
  const status = getBlogStatus(data);
  const statusToUpdate =
    status === BlogStatus.Published ? BlogStatus.Draft : BlogStatus.Published;

  const confirmDialog = {
    maxWidth: "xs" as const,
    open: isOpen,
    onClose,
    onConfirm: () =>
      publish({
        id: id.toString(),
        isPublished: !isPublished,
      }),
    title: `คุณต้องการเปลี่ยนสถานะเป็น ${formatBlogStatus(
      statusToUpdate
    )} หรือไม่`,
    confirmMessage: "ใช่",
    cancelMessage: "ไม่ใช่",
    loading: isLoading,
  };

  return (
    <FormControlLabel
      control={
        <>
          <Switch
            value={id}
            checked={isPublished}
            onChange={onChangeStatus}
            disabled={isOpen}
          />
          <ConfirmDialog {...confirmDialog} />
        </>
      }
      label={
        <Typography variant="body2">{formatBlogStatus(status)}</Typography>
      }
      onClick={(event) => {
        event.stopPropagation();
      }}
    />
  );
}
